import React, { useState, useEffect } from "react";
import { getCashBoxes, deleteById } from "../../../services/cash-box.service";
import { useNavigate, NavigateFunction } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridActionsCellItem,
  GridRowId,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {Chip, FormControl, InputLabel, Link, MenuItem, Select, SelectChangeEvent, TextField} from "@mui/material";
import IPage from "../../../types/page.type";
import {Box, Breadcrumbs, Grid, Typography, Button} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ICashBox from "../../../types/cash-box.type";
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import {CashBoxTypeNames} from "../../../types/enumeration/CashBoxTypeNames";
import {CashBoxType} from "../../../types/enumeration/CashBoxType";
import moment from "moment";
import {CashBoxStatus} from "../../../types/enumeration/CashBoxStatus";
import {CashBoxStatusNames} from "../../../types/enumeration/CashBoxStatusNames";
import {CashBoxStatusColors} from "../../../types/enumeration/CashBoxStatusColors";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import showNotification from "../../utils/Notify";
import ConfirmationModal from "../../utils/ConfirmationModal";

const CashBoxesList: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const [cashBoxesPage, setCashBoxesPage] = useState<IPage>({ content: [], totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState<any>("asc");
  const [idFilter, setIdFilter] = useState( "");
  const [keywordFilter, setKeywordFilter] = useState( "");
  const [statusFilter, setStatusFilter] = useState( "-1");
  const [excludeClosedFilter, setExcludeClosedFilter] = useState( "true");
  const [selectedCashBoxId, setSelectedCashBoxId] = React.useState<any>();
  const [confirmationDeleteModalOpen, setConfirmationDeleteModalOpen] = React.useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns: GridColDef<ICashBox>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'created',
      headerName: 'Fecha',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.created).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'worker',
      headerName: 'Recurso Asignado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:170,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{`${params.row.assignedWorker.lastName + ', ' + params.row.assignedWorker.name}`}</span>;
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={CashBoxStatusColors[CashBoxStatus[params.row.status as keyof typeof CashBoxStatus]]}
                     label={CashBoxStatusNames[CashBoxStatus[params.row.status as keyof typeof CashBoxStatus]]}
                     sx={{width: 160, fontWeight: 'bold'}}/>;
      }
    },
    {
      field: 'account',
      headerName: 'Orden de Servicio',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.serviceOrder?.number}</span>;
      }
    },
    {
      field: 'rol',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{CashBoxTypeNames[CashBoxType[params.row.type as keyof typeof CashBoxType]]}</span>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      cellClassName: 'actions',
      getActions: (params) => {
        const { id, status } = params.row;
        const actions = [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        ];
        if (status === CashBoxStatus.IN_PROGRESS) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteForeverIcon />}
              label="Delete"
              className="textPrimary"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />
          )
        }
        return actions;
      },
    },
  ];

  const handleAddClick = () => {
    navigate('create');
  };

  const handleEditClick = (id: GridRowId) => () => {
    navigate('edit/'+id)
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setSelectedCashBoxId(id);
    setConfirmationDeleteModalOpen(true);
  };

  const handleConfirmationDeleteModalClose = () => {
    setSelectedCashBoxId(null);
    setConfirmationDeleteModalOpen(false);
  }

  const onConfirmDelete = () => {
    deleteById(selectedCashBoxId)
      .then(() => {
        showNotification({type: 'success', message: 'Caja eliminada correctamente.'});
        handleConfirmationDeleteModalClose();
        fetchCashBoxes()
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  const handleChangeIdFilter = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setIdFilter(event.target.value)
  };

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setKeywordFilter(event.target.value)
  };

  const handleChangeStatus = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
    if (event.target.value === "-1") {
      setExcludeClosedFilter("true")
    } else {
      setExcludeClosedFilter("false")
    }
  };

  const handleChangeExcludeClosed = (event: SelectChangeEvent) => {
    setExcludeClosedFilter(event.target.value)
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchCashBoxes(newPaginationModel.page);
  };

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    let field = sortModel[0] ? sortModel[0].field : "id";
    let order = sortModel[0] ? sortModel[0].sort : "asc";
    fetchCashBoxes(paginationModel.page, paginationModel.pageSize, field, order);
    setSortColumn(field);
    setSortDirection(order);
  }, []);

  const fetchCashBoxes = async (page: number = paginationModel.page, size: number = paginationModel.pageSize, sort: string = sortColumn, order: any = sortDirection) => {
    setLoading(true);
    type Param = {[key: string] : string}
    const params: Param = {}
    params.page = String(page);
    params.size = String(size);
    params.sort = sort;
    params.order = order;

    if (idFilter) {
      params.id = idFilter;
    }

    if (keywordFilter) {
      params.keyword = keywordFilter;
    }

    if (statusFilter !== '-1') {
      params.status = statusFilter;
    }

    if (excludeClosedFilter) {
      params.excludeClosed = excludeClosedFilter;
    }

    getCashBoxes(params).then( (response) => {
      setCashBoxesPage(response.data)
      setLoading(false);
    })
  };

  useEffect(() => {
    fetchCashBoxes();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', px: { xs: 1} }}>
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <LocalAtmIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                Control Financiero
              </Typography>
              <Typography level="h4" component="h1">
                Cajas
              </Typography>
              <Typography level="h4" component="h1">
                <Link
                  underline={"none"}
                  onClick={() => handleAddClick()}
                  sx={{ cursor: 'pointer' }}
                >
                  <AddCircleIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} /> Crear Nuevo
                </Link>
              </Typography>
            </Breadcrumbs>
          </Box>

          <ConfirmationModal
            open={confirmationDeleteModalOpen}
            onConfirm={onConfirmDelete}
            onCancel={handleConfirmationDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta Caja?"
            isDeleteAction={true}
          />

          <Grid
            container
            rowSpacing={2}
            columnSpacing={1}
            paddingLeft={0.5}
            paddingRight={0.5}
            marginBlock={1}
            marginInline={0}
            sx={{ width: '100%' }}
          >
            <Grid xl={1} md={2} sm={2} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="idFilter"
                  label="ID"
                  name="idFilter"
                  variant="filled"
                  type="text"
                  onChange={handleChangeIdFilter}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchCashBoxes();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={5} md={10} sm={10} xs={12}>
              <CssVarsProvider>
                <TextField
                  fullWidth
                  id="keyword"
                  label="Buscar por Orden de Servicio y/o Recurso Asignado"
                  name="keyword"
                  variant="filled"
                  type="text"
                  onChange={handleChangeKeyword}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      fetchCashBoxes();
                      ev.preventDefault();
                    }
                  }}
                  slotProps={{ inputLabel: {shrink: true}}}
                />
              </CssVarsProvider>
            </Grid>
            <Grid xl={2} md={6} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="type-label" shrink>Estado</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    label="type"
                    value={statusFilter}
                    onChange={handleChangeStatus}
                  >
                    <MenuItem key="-1" value="-1">Todos</MenuItem>
                    {Object
                      .keys(CashBoxStatus)
                      .map((key) => (
                        <MenuItem key={key} value={CashBoxStatus[key as keyof typeof CashBoxStatus]}>
                          {CashBoxStatusNames[CashBoxStatus[key as keyof typeof CashBoxStatus]]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>
            <Grid xl={2} md={3} sm={6} xs={12}>
              <CssVarsProvider>
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="excludeClosed-label" shrink>Ocultar Cerradas</InputLabel>
                  <Select
                    labelId="excludeClosed-label"
                    id="excludeClosed"
                    name="excludeClosed"
                    label="excludeClosed"
                    value={excludeClosedFilter}
                    onChange={handleChangeExcludeClosed}
                    disabled={statusFilter !== '-1'}
                  >
                    <MenuItem key="true" value="true">Si</MenuItem>
                    <MenuItem key="false" value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </CssVarsProvider>
            </Grid>
            <Grid xl={2} md={3} sm={12} xs={12}>
              <Button
                variant="outlined"
                fullWidth
                style={{height: 56}}
                endDecorator={<SearchIcon />}
                onClick={() => fetchCashBoxes()}
              >
                Buscar
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ width: '100%', height: 632, typography: 'body1' }} ml={1} mt={0} pr={2}>
            <CssVarsProvider>
              <DataGrid
                rows={cashBoxesPage.content}
                columns={columns}
                paginationModel={paginationModel}
                rowCount={cashBoxesPage.totalElements}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                paginationMode="server"
                onPaginationModelChange={handlePaginationModelChange}
                loading={loading}
                disableRowSelectionOnClick
              />
            </CssVarsProvider>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CashBoxesList;
